import { isSubscriptionExpiredError } from "../../utils/subscriptionExpiredDialog";
import React, { useEffect, useState } from "react";
import PageTitle from "../../components/PageTitle";
import { useAxiosPost } from "../../hooks/useAxios";
import {
  Link,
  useLocation,
  useParams,
  useNavigationType,
} from "react-router-dom";
import TenantLink from "../../components/TenantLink";
import { motion, AnimatePresence } from "framer-motion";
import { ServiceType } from "../../utils/enums";
import { Table } from "../../components/Table";
import moment from "moment";
import {
  deleteNotices,
  fetchData,
  postData,
} from "../../api/apiService";
import { toast } from "react-toastify";
import {
  Breadcrumb,
  Button,
  DatePicker,
  Input,
  Modal,
  Popover,
  Select,
  Tag,
  Tooltip,
} from "antd";
import WarningImage from "../../assets/images/Warning.png";
import { FaFilter, FaList } from "react-icons/fa";
import {
  CalendarOutlined,
  CloseOutlined,
  DeleteFilled,
} from "@ant-design/icons";
import {
  getClientDocType,
  getClientsFields,
  getDocType,
} from "../../utils/helpers";
import DashboardButton from "../../components/DashboardButton";
import UserFilterDropdown, {
  ALL_USERS,
  toQueryParam as userFilterToQueryParam,
} from "../../components/UserFilterDropdown";
import {
  ArrowRight,
  FilterIcon,
  ChevronDown,
  ChevronUp,
  Download,
  CheckCircle,
} from "lucide-react";
import ClientAutoComplete from "../../components/ClientAutoComplete";
import TableV2 from "../../components/TableV2";
import DownloadDataButton from "../../components/DownloadDataButton";
import DeleteAllNoticesButton from "../../components/DeleteAllNoticesButton";
import BulkStatusButton from "../../components/BulkStatusButton";
import { EVENTS, LAMBDA, DOCUMENTS } from "../../api/endpointMap";

// Maps the URL `?type=` view to the backend `notice_mode` query param.
// Same mapping GST uses (GstNoticesList.jsx:80) so the three portals filter
// their notice-type views identically, server-side.
const getNoticeMode = (type) => {
  const map = {
    total_notices: "total",
    last_3_days_notices: "last_3_days",
    last_15_days_notices: "last_15_days",
    open_notices: "open",
    over_due_notices: "over_due",
    due_7_days_notices: "due_within_7",
  };
  return map[type] || null;
};

export const TDSNoticeList = ({ serviceType }) => {
  const { clientId } = useParams();
  const [notices, setNotices] = useState([]);
  // Starts TRUE: this page always fetches on mount, so the first paint
  // must show the loader - initialising false flashed "No data available"
  // before the fetch's own setLoading(true) ran.
  const [loading, setLoading] = useState(true);
  const [selectedNoticeIds, setSelectedNoticeIds] = useState([]);
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [formTypeOptions, setFormTypeOptions] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [financialYearOptions, setFinancialYearOptions] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [statusOptions, setStatusOptions] = useState([
    {
      label: "Open",
      value: "Open",
    },
    {
      label: "Close",
      value: "Close",
    },
  ]);

  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [processedClients, setProcessedClients] = useState([]);
  // User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). ALL_USERS sentinel
  // → omit assigned_user_id from the payload; BE applies role-default scope.
  const [userFilter, setUserFilter] = useState(ALL_USERS);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const noticeDibaledType = queryParams.get("disabledType") ?? "active";
  const dueDateParam = queryParams.get("due_date");
  const clientName = queryParams.get("client_name");
  const noticeType = queryParams.get("type");

  //Pagination Items
  const [limitStart, setLimitStart] = useState(0);
  const [limit, setLimit] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);

  // Storage key for filter persistence
  const FILTER_STORAGE_KEY = `tds_notices_filters_${clientId || "all"}`;

  // Detect navigation type: POP = browser back/forward, PUSH/REPLACE = fresh navigation
  const navigationType = useNavigationType();

  // Load saved filters from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert date strings back to moment objects if needed
        if (parsed.issuedDate && Array.isArray(parsed.issuedDate)) {
          parsed.issuedDate = parsed.issuedDate.map((d) =>
            d ? moment(d) : null
          );
        }
        if (parsed.dueDate && Array.isArray(parsed.dueDate)) {
          parsed.dueDate = parsed.dueDate.map((d) => (d ? moment(d) : null));
        }
        return parsed;
      }
    } catch (e) {
      console.error("Error loading saved filters:", e);
    }
    return null;
  };

  const getDefaultFilters = () => {
    return noticeType == "failed_login"
      ? { client_name: null }
      : {
          issuedDate: null,
          dueDate: null,
          quarter: null,
          formType: null,
          response_status: null,
          status: null,
          financialYear: null,
          client_name: null,
        };
  };

  // Clear filters from sessionStorage (used when navigating via breadcrumb)
  const clearSavedFilters = () => {
    try {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    } catch (e) {
      console.error("Error clearing saved filters:", e);
    }
  };

  // Only restore filters on browser back (POP), not on fresh navigation (PUSH/REPLACE)
  const [filterValues, setFilterValues] = useState(() => {
    if (navigationType === "POP") {
      // Browser back/forward - restore saved filters
      const savedFilters = getSavedFilters();
      return savedFilters || getDefaultFilters();
    } else {
      // Fresh navigation (clicking card/link) - clear saved filters and start fresh
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
      return getDefaultFilters();
    }
  });

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      const filtersToSave = { ...filterValues };
      if (filtersToSave.issuedDate && Array.isArray(filtersToSave.issuedDate)) {
        filtersToSave.issuedDate = filtersToSave.issuedDate.map((d) =>
          d ? d.format() : null
        );
      }
      if (filtersToSave.dueDate && Array.isArray(filtersToSave.dueDate)) {
        filtersToSave.dueDate = filtersToSave.dueDate.map((d) =>
          d ? d.format() : null
        );
      }
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (e) {
      console.error("Error saving filters:", e);
    }
  }, [filterValues, FILTER_STORAGE_KEY]);

  // Get filtered client name for breadcrumb display
  const getFilteredClientName = () => {
    return filterValues?.client_name?.trim() || null;
  };

  const formatDate = (date) => {
    if (!date) return "";

    if (typeof date.format === "function") {
      return date.format("YYYY-MM-DD");
    }

    if (
      Object.prototype.toString.call(date) === "[object Date]" &&
      !isNaN(date.getTime())
    ) {
      return date.toISOString().slice(0, 10);
    }

    const m = moment(date);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  };

  const { RangePicker } = DatePicker;
  const { Option } = Select;

  const getColumnWidths = () => {
    if (noticeType == "failed_login") {
      return {
        client_name: "200px",
        portal: "150px",
        created: "250px",
      };
    }
    return {
      client_name: "20%",
      financial_year: "18%",
      form_type: "18%",
      issued_date: "13%",
      due_date: "13%",
      quarter: "10%",
      status: "10%",
    };
  };

  const columnWidths = getColumnWidths();

  const renderFilterRow = () => {
    if (noticeType == "failed_login") {
      return null;
    }

    const activeFiltersCount = Object.values(filterValues).filter(
      (v) => v && v !== ""
    ).length;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-lg mb-4 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/3 to-indigo-600/3"></div>

        <div className="relative bg-white/90 backdrop-blur-sm border-b border-blue-200/50 px-6 py-3">
          <div className="flex items-center justify-between">
            <motion.div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setIsExpanded(!isExpanded)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="bg-blue-800 p-2 rounded-lg shadow-md">
                <FilterIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                  Advanced Filters
                  {activeFiltersCount > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-800 text-white text-xs px-2 py-1 rounded-full font-medium"
                    >
                      {activeFiltersCount}
                    </motion.div>
                  )}
                </h3>
                <p className="text-xs text-blue-600">
                  {isExpanded ? "Click to collapse" : "Click to expand filters"}
                </p>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={clearFilters}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-all duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Clear All
                </motion.button>
              )}

              <motion.button
                onClick={() => setIsExpanded(!isExpanded)}
                className="bg-blue-800 text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-blue-900 transition-all duration-200 shadow-md flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{isExpanded ? "Collapse" : "Expand"}</span>
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              </motion.button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="relative p-6">
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className="grid  lg:grid-cols-7 gap-4"
                >
                  {/* Client Name Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Client Name
                    </span>
                    <div className="relative">
                      <ClientAutoComplete
                        serviceType={serviceType}
                        handleSelect={(value) =>
                          handleFilterValueChange("client_name", value)
                        }
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                      />
                    </div>
                  </motion.div>

                  {/* Financial Year Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Financial Year
                    </span>
                    <div className="relative">
                      <Input
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        value={filterValues.financialYear}
                        onChange={(e) =>
                          handleFilterValueChange(
                            "financialYear",
                            e.target.value
                          )
                        }
                        placeholder="Filter year"
                        allowClear
                        prefix={<div className="text-blue-400">📅</div>}
                      />
                    </div>
                  </motion.div>

                  {/* Form Type Filter */}
                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Form Type
                    </span>
                    <div className="relative">
                      <Input
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        value={filterValues.formType}
                        onChange={(e) =>
                          handleFilterValueChange("formType", e.target.value)
                        }
                        placeholder="Filter form"
                        allowClear
                        prefix={<div className="text-blue-400">📋</div>}
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Issue Date
                    </span>
                    <div className="relative">
                      <RangePicker
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        format="DD/MM/YYYY"
                        value={filterValues.issuedDate}
                        onChange={(dates) =>
                          handleFilterValueChange("issuedDate", dates)
                        }
                        placeholder={["From", "To"]}
                        size="small"
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Due Date
                    </span>
                    <div className="relative">
                      <RangePicker
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        format="DD/MM/YYYY"
                        value={filterValues.dueDate}
                        onChange={(dates) =>
                          handleFilterValueChange("dueDate", dates)
                        }
                        placeholder={["From", "To"]}
                        size="small"
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Quarter
                    </span>
                    <div className="relative">
                      <Input
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        value={filterValues.quarter}
                        onChange={(e) =>
                          handleFilterValueChange("quarter", e.target.value)
                        }
                        placeholder="Filter quarter"
                        allowClear
                        prefix={<div className="text-blue-400">🗓️</div>}
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    className="group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <span className="block text-sm font-semibold text-blue-800  flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      Status
                    </span>
                    <div className="relative">
                      <Input
                        className="w-full h-10 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white group-hover:shadow-md"
                        value={filterValues.status}
                        onChange={(e) =>
                          handleFilterValueChange("status", e.target.value)
                        }
                        placeholder="Filter quarter"
                        allowClear
                        prefix={<div className="text-blue-400">🗓️</div>}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="h-1 bg-gradient-to-r from-blue-600 to-indigo-600"></div>
      </motion.div>
    );
  };

  const columns =
    noticeType == "failed_login"
      ? [
          {
            label: "Portal",
            key: "portal",
            filterable: false,
            filterName: "portal",
          },
          {
            label: "Client",
            key: "client_name",
            filterable: false,
            filterName: "client_name",
          },
          {
            label: "Initiated",
            key: "creation",
            filterable: false,
            filterName: "creation",
            render: (row) => {
              const formattedDate = row["creation"]
                ? moment(row["creation"]).format("MMMM Do YYYY, h:mm:ss a")
                : "-";

              return row["creation"] ? (
                <div className="flex items-center h-14 justify-center">
                  <Tag
                    icon={<CalendarOutlined />}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "200px",
                      padding: "5px 10px",
                    }}
                  >
                    {formattedDate}
                  </Tag>
                </div>
              ) : (
                <p>-</p>
              );
            },
          },
        ]
      : [
          // { label: "GSTIN", key: "name" },
          {
            label: "Client Name",
            key: "client_name",
            filterable: true,
            filterType: "autocomplete",
            filterName: "client_name",
          },
          {
            label: "Financial Year",
            key: "fy",
            filterable: true,
            filterType: "text",
            filterName: "financialYear",
          },
          {
            label: "Form Type",
            key: "form_type",
            filterable: true,
            filterType: "text",
            filterName: "formType",
          },
          {
            label: "Issued Date",
            key: "issue_date",
            filterable: true,
            filterType: "date",
            filterName: "issuedDate",
            render: (row) => {
              const date = row?.issue_date;
              const formattedDate = date
                ? date.split("-").reverse().join("-")
                : null;
              return formatDate ? (
                <div className="flex justify-center mr-4">
                  <Tag color="#108ee9">{formattedDate}</Tag>
                </div>
              ) : (
                <div className="flex justify-center mr-4">-</div>
              );
            },
          },
          {
            label: "Due Date",
            key: "due_date",
            filterable: true,
            filterType: "date",
            filterName: "dueDate",
            render: (row) => {
              const date = row?.due_date;
              const formattedDate = date
                ? date.split("-").reverse().join("-")
                : null;
              return formattedDate ? (
                <div className="flex justify-center mr-4">
                  {" "}
                  <Tag color="#108ee9">{formattedDate}</Tag>
                </div>
              ) : (
                <div className="flex justify-center mr-4">-</div>
              );
            },
          },
          {
            label: "Quarter",
            key: "quarter",
            filterable: true,
            filterType: "select",
            filterName: "quarter",
            filterOptions: [
              { label: "Q1", value: "Q1" },
              { label: "Q2", value: "Q2" },
              { label: "Q3", value: "Q3" },
              { label: "Q4", value: "Q4" },
            ],
          },
          {
            label: "Response Status",
            key: "response_status",
            filterable: true,
            filterType: "select",
            filterName: "response_status",
            filterOptions: [
              { label: "Pending", value: "Pending" },
              { label: "Response Generated", value: "Response Generated" },
              { label: "Response Finalized", value: "Response Finalized" },
              { label: "Submitted", value: "Submitted" },
            ],
            render: (row) => {
              const statusColors = {
                Pending: "#d9d9d9",
                "Response Generated": "#faad14",
                "Response Finalized": "#1890ff",
                Submitted: "#52c41a",
              };
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Tag
                    color={statusColors[row?.response_status] || "#d9d9d9"}
                    style={{ padding: "2px 10px" }}
                  >
                    {row?.response_status || "Pending"}
                  </Tag>
                </div>
              );
            },
          },
          {
            label: "Portal Status",
            key: "notice_status",
            filterable: true,
            filterType: "select",
            filterName: "status",
            filterOptions: [
              { label: "Open", value: "Open" },
              { label: "OverDue", value: "OverDue" },
              { label: "Close", value: "Close" },
            ],
          },
        ];

  const [fetchTDSCount] = useAxiosPost(DOCUMENTS.GET_COUNT);

  const buildFilters = () => {
    const filters = [];

    if (noticeType != "failed_login") {
      filters.push(["disabled", "=", noticeDibaledType === "active" ? 0 : 1]);

      if (clientId) {
        filters.push(["client", "=", clientId]);
      }

      if (dueDateParam) {
        filters.push(["due_date", "=", dueDateParam]);
      }

      if (noticeType == "last_3_days_notices") {
        const today = new Date();
        const previousDate = new Date();
        previousDate.setDate(today.getDate() - 2);
        filters.push([
          "creation",
          "between",
          [formatDate(previousDate), formatDate(today)],
        ]);
      }

      if (noticeType == "last_15_days_notices") {
        const today = new Date();
        const pastDate = new Date();
        pastDate.setDate(today.getDate() - 15);
        filters.push(["issue_date", ">=", formatDate(pastDate)]);
      }

      if (noticeType == "over_due_notices") {
        filters.push(["notice_status", "=", "OverDue"]);
      }

      if (noticeType == "open_notices") {
        filters.push(["notice_status", "=", "Open"]);
      }

      if (["over_due_notices", "due_7_days_notices", "open_notices"].includes(noticeType)) {
        filters.push(["response_status", "!=", "Submitted"]);
      }

      if (filterValues.issuedDate && filterValues.issuedDate.length === 2) {
        const startDate = formatDate(filterValues.issuedDate[0]);
        const endDate = formatDate(filterValues.issuedDate[1]);
        filters.push(["issue_date", "between", [startDate, endDate]]);
      }

      if (filterValues.dueDate && filterValues.dueDate.length === 2) {
        const startDate = formatDate(filterValues.dueDate[0]);
        const endDate = formatDate(filterValues.dueDate[1]);
        filters.push(["due_date", "between", [startDate, endDate]]);
      }

      if (filterValues.financialYear) {
        filters.push(["fy", "like", `%${filterValues.financialYear}%`]);
      }

      if (filterValues.formType) {
        filters.push(["form_type", "like", `%${filterValues.formType}%`]);
      }

      if (filterValues.quarter) {
        filters.push(["quarter", "like", `%${filterValues.quarter}%`]);
      }

      if (filterValues.status) {
        filters.push(["notice_status", "=", filterValues.status]);
      }

      if (filterValues.response_status) {
        filters.push(["response_status", "=", filterValues.response_status]);
      }

      if (filterValues.client_name) {
        filters.push(["client_name", "like", `%${filterValues.client_name}%`]);
      }
    } else {
      filters.push(["portal", "=", "TDS"]);
      filters.push(["disabled", "=", 0]);
      if (filterValues.client_name) {
        filters.push(["client_name", "=", filterValues.client_name]);
      }
    }

    return filters;
  };

  const getTDSNoticeCount = (docType) => {
    // Failed logins: the dedicated /tds/failed-logins list call already sets
    // totalRecords from its `total_records`, so skip the legacy
    // `POST /documents/count` here (it 404s on FastAPI).
    if (docType === "Login Failed") return;

    const filters = buildFilters();

    const payload = {
      doctype: docType,
      filters: filters,
    };

    if (noticeType == "due_7_days_notices") {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + 7);
      payload.or_filters = [
        ["due_date", "between", [formatDate(today), formatDate(futureDate)]],
        ["due_date", "is", "not set"],
      ];
    }

    fetchTDSCount({
      payload,
      cb: (data) => {
        setTotalRecords(data?.message);
      },
    });
  };

  // Single source of truth for the client-side row filter (notice-type view +
  // column filters). This predicate previously existed as TWO byte-identical
  // copies — one in getTDSNoticeList, one in fetchAllForExport — so any rule
  // change had to be made twice and the export could silently drift from the
  // on-screen table. Unified here; both call sites now share it.
  //
  // Overdue rule: a quarter is overdue when its DUE DATE HAS PASSED. It used to
  // test `notice_status === "OverDue"` only, but nothing ever writes that value
  // (the column is created as "Open" and only ever read), so the Overdue view
  // was always empty. This now matches the backend dashboard card, which was
  // corrected the same way — otherwise the card would show a count and clicking
  // it would land on an empty list.
  const rowMatchesFilters = (row) => {
    const todayMs = new Date().setHours(0, 0, 0, 0);
    const dueMs = row.due_date ? new Date(row.due_date).getTime() : null;

    if (dueDateParam && row.due_date !== dueDateParam) return false;

    if (noticeType === "over_due_notices") {
      // A closed notice is finished business and is never overdue, no matter
      // how far past its due date. Checked BEFORE the date test and kept in
      // step with the backend card + list filter, so the tile's count and
      // these rows always agree.
      if (row.notice_status === "Close") return false;
      const isOverdue =
        row.notice_status === "OverDue" || (dueMs !== null && dueMs < todayMs);
      if (!isOverdue) return false;
    }
    if (noticeType === "open_notices" && row.notice_status !== "Open") return false;
    if (["over_due_notices", "due_7_days_notices", "open_notices"].includes(noticeType) && row.response_status === "Submitted") return false;
    if (noticeType === "due_7_days_notices") {
      // Rows with NO due date deliberately stay in this view. That is not an
      // oversight: the backend card counts `due_date IS NULL` as due-within-7
      // (dashboard_cards.py), and the legacy Frappe count sent
      // `["due_date", "is", "not set"]` as an or-filter. Excluding them here
      // would make the list contradict the number on the card it came from.
      if (dueMs !== null) {
        const horizonMs = todayMs + 7 * 24 * 60 * 60 * 1000;
        if (dueMs < todayMs || dueMs > horizonMs) return false;
      }
    }
    if (filterValues.financialYear && !(row.fy || "").includes(filterValues.financialYear)) return false;
    if (filterValues.formType && !(row.form_type || "").toLowerCase().includes(filterValues.formType.toLowerCase())) return false;
    if (filterValues.quarter && !(row.quarter || "").includes(filterValues.quarter)) return false;
    if (filterValues.status && row.notice_status !== filterValues.status) return false;
    if (filterValues.response_status && row.response_status !== filterValues.response_status) return false;
    if (filterValues.client_name && !(row.client_name || "").toLowerCase().includes(filterValues.client_name.toLowerCase())) return false;
    if (filterValues.issuedDate && filterValues.issuedDate.length === 2) {
      const start = formatDate(filterValues.issuedDate[0]);
      const end = formatDate(filterValues.issuedDate[1]);
      if (!row.issue_date || row.issue_date < start || row.issue_date > end) return false;
    }
    if (filterValues.dueDate && filterValues.dueDate.length === 2) {
      const start = formatDate(filterValues.dueDate[0]);
      const end = formatDate(filterValues.dueDate[1]);
      if (!row.due_date || row.due_date < start || row.due_date > end) return false;
    }
    return true;
  };

  // Fetch ALL records (all pages) matching current filters — used by
  // TableV2 PDF/CSV download buttons. Loops in batches of 500 until
  // last parent batch returns < 500. Mirrors getTDSNoticeList endpoint
  // + flatten + client-side filter logic so the export shape matches
  // exactly what the on-screen table shows.
  const fetchAllForExport = async () => {
    const PAGE_SIZE = 500;
    const all = [];
    const userParam = userFilterToQueryParam(userFilter);

    if (noticeType !== "failed_login") {
      let start = 0;
      while (true) {
        const restParams = {
          start,
          page_length: PAGE_SIZE,
          include_quarters: true,
        };
        if (clientId) restParams.client = clientId;
        if (userParam !== undefined) restParams.assigned_user_id = userParam;
        // Notice-type view is filtered SERVER-SIDE (mirrors GST/IT). Previously
        // it was only applied client-side to the rows of the fetched page, so
        // views like "Due within 7 days" / "Overdue" came back empty on any org
        // with more quarters than one page, while the dashboard card counted
        // across all rows. rowMatchesFilters still runs for the column filters.
        const mode = getNoticeMode(noticeType);
        if (mode) restParams.notice_mode = mode;

        const resp = await fetchData(
          "/api/v1/tds/financial-notices",
          restParams,
        );
        const parents = resp?.data?.records || [];
        const flattened = parents.flatMap((p) =>
          (p.quarters || []).map((q) => ({
            name: q.id,
            fy: q.financial_year,
            quarter: q.quarter_name,
            form_type: q.form_type || "",
            issue_date: q.issue_date,
            due_date: q.due_date,
            response_status: q.response_status,
            notice_status: q.notice_status,
            client: q.client_id,
            client_name: q.client_name,
            creation: q.creation,
            notice_type: "TDS Notice",
          })),
        );

        const visible = flattened.filter(rowMatchesFilters);

        all.push(...visible);
        if (parents.length < PAGE_SIZE) break;
        start += PAGE_SIZE;
      }
      return all;
    }

    // failed_login now uses the dedicated FastAPI endpoint (mirrors GST/IT);
    // the legacy Frappe resource path 404s. Page through until a short batch.
    let start = 0;
    while (true) {
      const params = { start, page_length: PAGE_SIZE };
      if (filterValues?.client_name && String(filterValues.client_name).trim()) {
        params.client_name = String(filterValues.client_name).trim();
      }
      if (userParam !== undefined) params.assigned_user_id = userParam;

      const resp = await fetchData("/api/v1/tds/failed-logins", params);
      const batch = (resp?.result?.data || []).map((item) => ({
        ...item,
        notice_type: "TDS Notice",
      }));
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
      start += PAGE_SIZE;
    }
    return all;
  };

  const getTDSNoticeList = async (start, itemsPerPage) => {
    try {
      setLoading(true);

      // §7.2: scope to a specific user / Unassigned / All. ALL_USERS
      // → omit param entirely so BE auto-scopes per role.
      const userParam = userFilterToQueryParam(userFilter);

      // Quarter-level listing (everything except `failed_login`) now
      // goes through the FastAPI `/tds/financial-notices?include_quarters=true`
      // endpoint — the legacy Frappe `GET /Quarter` + `POST /documents/count`
      // pair returned 404 on FastAPI. Field-name mapping below adapts the
      // BE shape (`id, quarter_name, financial_year, client_id`) to the
      // Frappe-shaped names the table columns still expect
      // (`name, quarter, fy, client`). `failed_login` keeps its legacy
      // path until that surface gets its own FastAPI port.
      if (noticeType !== "failed_login") {
        const restParams = {
          start,
          page_length: itemsPerPage,
          include_quarters: true,
        };
        if (clientId) restParams.client = clientId;
        if (userParam !== undefined) restParams.assigned_user_id = userParam;
        // Notice-type view is filtered SERVER-SIDE (mirrors GST/IT). Previously
        // it was only applied client-side to the rows of the fetched page, so
        // views like "Due within 7 days" / "Overdue" came back empty on any org
        // with more quarters than one page, while the dashboard card counted
        // across all rows. rowMatchesFilters still runs for the column filters.
        const mode = getNoticeMode(noticeType);
        if (mode) restParams.notice_mode = mode;

        const resp = await fetchData(
          "/api/v1/tds/financial-notices",
          restParams,
        );
        const parents = resp?.data?.records || [];
        const flattened = parents.flatMap((p) =>
          (p.quarters || []).map((q) => ({
            name: q.id,
            fy: q.financial_year,
            quarter: q.quarter_name,
            form_type: q.form_type || "",
            issue_date: q.issue_date,
            due_date: q.due_date,
            response_status: q.response_status,
            notice_status: q.notice_status,
            client: q.client_id,
            client_name: q.client_name,
            creation: q.creation,
            notice_type: "TDS Notice",
          })),
        );

        // Apply the noticeType / filterValues filters client-side now
        // that the BE returns the full Quarter shape per parent. Keeps
        // the existing column-level filters working without porting the
        // full Frappe filter array to BE query params.
        const visible = flattened.filter(rowMatchesFilters);

        setNotices(visible);
        setTotalRecords(visible.length);

        if (visible.length > 0) {
          const uniqueFinancialYears = [...new Set(visible.map((i) => i.fy))].filter(Boolean);
          const uniqueQuarters = [...new Set(visible.map((i) => i.quarter))].filter(Boolean);
          const uniqueFormTypes = [...new Set(visible.map((i) => i.form_type))].filter(Boolean);
          setQuarterOptions(uniqueQuarters);
          setFormTypeOptions(uniqueFormTypes);
          setFinancialYearOptions(uniqueFinancialYears);
        }
        return;
      }

      // Failed logins now use the dedicated FastAPI endpoint (mirrors GST/IT).
      // The legacy Frappe `GET /Login Failed?filters=...` + `POST
      // /documents/count` pair both 404 on FastAPI. This one call returns the
      // page (`result.data`) AND the total (`result.total_records`), so the
      // separate count call is skipped for this tab (see getTDSNoticeCount).
      const params = { start, page_length: itemsPerPage };
      if (filterValues?.client_name && String(filterValues.client_name).trim()) {
        params.client_name = String(filterValues.client_name).trim();
      }
      if (userParam !== undefined) params.assigned_user_id = userParam;

      const data = await fetchData("/api/v1/tds/failed-logins", params);

      const noticesData = (data?.result?.data || []).map((item) => ({
        ...item,
        notice_type: "TDS Notice",
      }));

      setNotices(noticesData || []);
      setTotalRecords(data?.result?.total_records || 0);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterOptions =
    noticeType == "failed_login"
      ? [{ name: "client_name", label: "Client Name" }]
      : [
          { name: "client_name", label: "Client Name" },
          { name: "dueDate", label: "Due Date" },
          { name: "issuedDate", label: "Issued Date" },
          { name: "financialYear", label: "Financial Year" },
          { name: "formType", label: "Form Type" },
          { name: "quarter", label: "Quarter" },
          { name: "status", label: "Status" },
        ];

  const handleFilterValueChange = (filterName, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const getNoticePageName = (type) => {
    switch (type) {
      case "open_notices":
        return "Open Notices";
      case "last_3_days_notices":
        return "Notices Received in last 3 days";
      case "due_7_days_notices":
        return "Due within 7 days";
      case "failed_login":
        return "Failed Logins";
      case "last_15_days_notices":
        return "Last 15 Days Notices";
      case "over_due_notices":
        return "Over Due Notices";
      default:
        return "Total Notices";
    }
  };

  useEffect(() => {
    // Quarter-path count now comes from the same /financial-notices
    // response inside getTDSNoticeList — only failed_login still goes
    // through the legacy doctype-count POST.
    if (noticeType === "failed_login") {
      getTDSNoticeCount("Login Failed");
    }
    getTDSNoticeList(limitStart, limit);
    setLimitStart(0);
  }, [
    filterValues.issuedDate,
    filterValues.dueDate,
    filterValues.financialYear,
    filterValues.formType,
    filterValues.status,
    filterValues.quarter,
    filterValues.response_status,
    filterValues.client_name,
    dueDateParam,
  ]);

  const clearFilters = () => {
    setFilterValues(
      noticeType == "failed_login"
        ? {
            client_name: null,
          }
        : {
            issuedDate: null,
            dueDate: null,
            quarter: null,
            formType: null,
            status: null,
            financialYear: null,
          }
    );
    setLimitStart(0);
  };

  // Core delete for a given id array. Extracted so BOTH the "Bulk Delete"
  // (selected ids) button AND the shared "Delete All Notices" button (every id
  // in the current list scope) route through the exact same endpoint/doctype
  // logic. Throws on failure; does NOT toast or refresh.
  const deleteNoticesByIds = async (ids) => {
    if (!ids || ids.length === 0) return;
    // Legacy `/api/v1/documents/delete` returned 404 on FastAPI —
    // mirror the GST + IT pattern (DELETE_NOTICES_GST /
    // DELETE_NOTICES_INCOME_TAX) and use the portal-scoped
    // `/api/v1/tds/delete-documents` endpoint. Payload key is
    // `docnames` (not `items`) per the BE handler in tds.py.
    await deleteNotices(DOCUMENTS.DELETE_NOTICES_TDS, {
      doctype: "Quarter",
      docnames: JSON.stringify(ids),
    });
  };

  // Post-delete refresh, shared by both delete paths.
  const refreshAfterDelete = async () => {
    setSelectedNoticeIds([]);
    await getTDSNoticeList(limitStart, limit);
    if (noticeType === "failed_login") {
      getTDSNoticeCount("Login Failed");
    }
  };

  // All ids in the CURRENT list scope (client + filters), sourced from the same
  // paginator export uses. Feeds "Delete All Notices".
  const fetchAllNoticeIdsForDelete = async () => {
    const rows = await fetchAllForExport();
    return (rows || []).map((r) => r.id || r.name).filter(Boolean);
  };

  const handleDeleteNotices = async () => {
    try {
      await deleteNoticesByIds(selectedNoticeIds);
      toast.info("Deleted Successfully");
    } catch (err) {
      toast.error(err);
    } finally {
      await refreshAfterDelete();
    }
  };

  const handleOk = () => {
    setIsDeleteModalOpen(false);
    handleDeleteNotices();
  };

  const handleCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const handlePageChange = (newLimitStart, newLimit) => {
    setLimitStart(newLimitStart);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setLimitStart(0);
  };

  // Legacy Frappe-era data fetch. The doctype-keyed REST endpoint
  // (`/TDS Client`) never existed on the FastAPI backend, so this call
  // has been returning 404s since the migration. The `clientsData`
  // state it populated is unread elsewhere — dead. Left as a no-op so
  // the call site at the init effect below doesn't need to be touched
  // and the console isn't spammed with 404s on every mount. Mirrors the
  // already-applied fix in `pages/income-tax/Eproceedings.jsx:349-358`.
  const getClientsData = async () => {};

  const handleFetchAllClientsNotices = async () => {
    setIsProcessingAll(true);

    try {
      let apiUrl = EVENTS.TDS_PROCESS_ALL;

      const response = await postData(apiUrl);

      setProcessedClients(response?.result?.client_names || []);
      setTimeout(() => {
        setShowSuccessModal(true);
      }, 2000);
    } catch (error) {
      // An expired plan is a handled state: the interceptor has already

      // shown the upgrade dialog, so neither the red console line nor the

      // generic toast belongs here.

      if (!isSubscriptionExpiredError(error)) {

        console.error("Error processing all clients:", error);

        toast.error("Failed to queue all clients for processing");

      }
    } finally {
      setTimeout(() => {
        setIsProcessingAll(false);
      }, 2000);
    }
  };

  const handleFetchAllFailedLoginClientsNotices = async () => {
    setIsProcessingAll(true);

    try {
      const apiUrl = LAMBDA.FETCH_FAILED_LOGIN;
      const payload = {
        portal: "TDS",
      };

      const response = await postData(apiUrl, payload);

      setProcessedClients(response?.result?.client_names || []);
      setTimeout(() => {
        setShowSuccessModal(true);
      }, 2000);
    } catch (error) {
      // An expired plan is a handled state: the interceptor has already

      // shown the upgrade dialog, so neither the red console line nor the

      // generic toast belongs here.

      if (!isSubscriptionExpiredError(error)) {

        console.error("Error processing all clients:", error);

        toast.error("Failed to queue all clients for processing");

      }
    } finally {
      setTimeout(() => {
        setIsProcessingAll(false);
      }, 2000);
    }
  };

  useEffect(() => {
    getTDSNoticeList(limitStart, limit);
    // userFilter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2): re-fetch on change.
  }, [clientId, limitStart, limit, userFilter]);

  useEffect(() => {
    getClientsData();
    if (noticeType === "failed_login") {
      getTDSNoticeCount("Login Failed");
    }
  }, []);

  return (
    <>
      <div className="flex items-center justify-between w-full">
        {clientId ? (
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax",
                    }}
                  >
                    {serviceType == "gstin"
                      ? "GST "
                      : serviceType == "tds"
                      ? "TDS"
                      : "Income Tax"}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin/clients"
                          : serviceType == "tds"
                          ? "/tds/clients"
                          : "/income-tax/clients",
                    }}
                  >
                    Clients{" "}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <Link className="exempt-from-readonly" to={-1}>
                    {clientName || "Client Details"}
                  </Link>
                ),
              },
              { title: `TDS Notices` },
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        ) : (
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                    onClick={clearSavedFilters}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax",
                    }}
                    onClick={clearSavedFilters}
                  >
                    {serviceType == "gstin"
                      ? "GST "
                      : serviceType == "tds"
                      ? "TDS"
                      : "Income Tax"}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <span
                    className="cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      clearSavedFilters();
                      setFilterValues(getDefaultFilters());
                    }}
                  >
                    {getNoticePageName(noticeType)}
                  </span>
                ),
              },
              // Show client name in breadcrumb when filtered
              ...(getFilteredClientName()
                ? [
                    {
                      title: (
                        <span className="text-blue-600 font-medium">
                          {getFilteredClientName()}
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        )}
        <DashboardButton serviceType={serviceType} />
      </div>

      <PageTitle
        title={clientId ? "TDS Notice Details" : getNoticePageName(noticeType)}
      />

      {noticeType === "total_notices" && (
        <div className="mt-4 mb-4">
          <div className="flex items-center">
            <div className="flex items-center">
              {selectedNoticeIds.length == 0 ? (
                <Tooltip title="Please select a notice">
                  <div>
                    <Button
                      type="primary"
                      icon={<DeleteFilled />}
                      className="mr-2"
                      disabled
                    >
                      Bulk Delete
                    </Button>
                  </div>
                </Tooltip>
              ) : (
                <Button
                  type={"primary"}
                  icon={<DeleteFilled />}
                  onClick={() => setIsDeleteModalOpen(true)}
                  className="mr-2"
                  disabled={selectedNoticeIds.length > 0 ? false : true}
                >
                  Bulk Delete
                </Button>
              )}
              <BulkStatusButton
                selectedIds={selectedNoticeIds}
                doctype="Quarter"
                onDone={refreshAfterDelete}
              />
              <DeleteAllNoticesButton
                fetchAllIds={fetchAllNoticeIdsForDelete}
                deleteByIds={deleteNoticesByIds}
                onDone={refreshAfterDelete}
                scopeHint="across all clients"
              />
            </div>
            <Button
              loading={isProcessingAll}
              icon={<Download className="w-4 h-4 mt-2" />}
              type="primary"
              onClick={handleFetchAllClientsNotices}
              style={{
                background: "#1890ff",
                borderColor: "#1890ff",
                border: "1px solid #fff",
                boxShadow: "0 0 16px rgba(30, 64, 175, 0.4",
                borderRadius: "8px",
                padding: "8px 16px",
                fontWeight: "600",
                color: "#fff",
                transition: "box-shadow 0.3s ease",
              }}
              disabled={isProcessingAll}
            >
              {isProcessingAll
                ? "Processing..."
                : `Fetch Notices for All Clients`}
            </Button>
          </div>
        </div>
      )}

      {noticeType == "failed_login" && (
        <div className="my-2">
          <Button
            loading={isProcessingAll}
            icon={<Download className="w-4 h-4 mt-2" />}
            type="primary"
            onClick={handleFetchAllFailedLoginClientsNotices}
            disabled={isProcessingAll}
          >
            {isProcessingAll
              ? "Processing..."
              : `Fetch notices for all Failed Login Clients`}
          </Button>
        </div>
      )}

      {/* Filter Controls - Always Visible */}
      {/* <div className="mt-4 mb-4">
        <div className="rounded-md bg-white shadow-sm">{renderFilterRow()}</div>
      </div> */}

      {/* {noticeType != "failed_login" && (
        <motion.p
          className="text-blue-600 font-medium"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Total Notices : {totalRecords}
        </motion.p>
      )} */}

      {/* User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). Self-suppresses
          for single-user orgs and for regular users. */}
      {/* Top margin matches the GST + IT strips (mt-4 + mb-2) so the
          Download Data + All Users controls sit clear of the header
          across all three portals. */}
      <div className="flex items-center justify-end gap-2 mt-4 mb-2">
        {/* Delete Notice on the per-client TDS notice list — matches the GST +
            IT client views. Only on a client's list (clientId present); the
            all-clients "total notices" view has its own delete block above.
            Reuses the existing handleDeleteNotices + confirm modal; the backend
            delete is org-scoped. Disabled until at least one row is selected. */}
        {clientId &&
          (selectedNoticeIds.length === 0 ? (
            <Tooltip title="Please select a notice">
              <div>
                <Button type="primary" icon={<DeleteFilled />} disabled>
                  Bulk Delete
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button
              type="primary"
              icon={<DeleteFilled />}
              onClick={() => setIsDeleteModalOpen(true)}
            >
              Bulk Delete
            </Button>
          ))}
        {clientId && (
          <>
            <BulkStatusButton
              selectedIds={selectedNoticeIds}
              doctype="Quarter"
              onDone={refreshAfterDelete}
            />
            <DeleteAllNoticesButton
              fetchAllIds={fetchAllNoticeIdsForDelete}
              deleteByIds={deleteNoticesByIds}
              onDone={refreshAfterDelete}
              scopeHint="for this client"
              className=""
            />
          </>
        )}
        <DownloadDataButton
          entity="notices-tds"
          noun="notices"
          selectedIds={selectedNoticeIds}
          selectedRows={(notices || []).filter((r) =>
            selectedNoticeIds.includes(r.id || r.name)
          )}
          fetchAllRecords={fetchAllForExport}
          fileBaseName="noticeai-tds-notices"
          title="TDS Notices"
        />
        <UserFilterDropdown
          value={userFilter}
          onChange={setUserFilter}
          portal={serviceType}
        />
      </div>

      <TableV2
        columns={columns}
        data={notices}
        /* Without this, TableV2 defaulted isLoading to false and showed
           "No data available" during the whole first fetch. */
        isLoading={loading}
        itemsPerPage={10}
        serviceType={serviceType}
        setLimitStart={setLimitStart}
        limitStart={limitStart}
        limit={limit}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        type={noticeType == "failed_login" ? "failed_login" : "notices"}
        rowRedirection={
          noticeType == "failed_login"
            ? `/${ServiceType.TDS}/failed-login-details`
            : `/${ServiceType.TDS}/notice-details`
        }
        selectedIds={selectedNoticeIds}
        setSelectedIds={setSelectedNoticeIds}
        filterValues={filterValues}
        handleFilterValueChange={handleFilterValueChange}
        statusOptions={statusOptions}
        title={getNoticePageName(noticeType)}
        onFetchAllRecords={fetchAllForExport}
      />

      <Modal
        title=""
        open={isDeleteModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
        zIndex={9999}
        style={{ top: -50 }}
      >
        <div className="flex items-center justify-center flex-col">
          <img src={WarningImage} width={250} height={250} />
          <p>Are you sure you want to Delete ?</p>
        </div>
      </Modal>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CheckCircle color="#52c41a" size={20} />
            <span>Successfully Queued</span>
          </div>
        }
        open={showSuccessModal}
        onOk={() => setShowSuccessModal(false)}
        onCancel={() => setShowSuccessModal(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setShowSuccessModal(false)}
          >
            Got It
          </Button>,
        ]}
        width={500}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: "16px", fontSize: "16px" }}>
            <strong>
              Notice fetching has been successfully queued for all clients.
            </strong>
          </p>

          <p style={{ marginBottom: "16px", color: "#666" }}>
            We're fetching the notices in the background. It will take some
            time, so please check back later for the results.
          </p>

          {processedClients.length > 0 && (
            <div>
              <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Queued for {processedClients.length} clients:
              </p>
              <div
                style={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  background: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                {processedClients.map((client, index) => (
                  <div key={index} style={{ padding: "2px 0" }}>
                    • {client}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
